import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { ApiError } from "../lib/api";
import {
  ADD_CONTACT_SHEET_TITLE,
  contactNameConflictMessage,
  CONTACT_NAME_FIELD_LABEL,
  CONTACT_NOTES_FIELD_LABEL,
  CONTACT_PHONE_OR_EMAIL_FIELD_LABEL,
  COULD_NOT_SAVE_ERROR,
  editContactSheetTitle,
  SAVE_CHANGES_LABEL,
} from "../lib/recordsCopy";
import type { ContactOut } from "../types";

export interface ContactFormSheetFields {
  name: string;
  contact: string | null;
  notes: string | null;
}

export interface ContactFormSheetProps {
  open: boolean;
  /** `"add"` (default) — the `ContactsView` header's `"+ Add"` sheet.
   * `"edit"` — `ContactDetailOverlay`'s admin-only "✎ Edit" affordance,
   * pre-filled from `contact`. Same single-sheet, mode-driven shape as
   * `AnimalFormSheet`/`ProjectFormSheet` (design-spec-animal-cards-
   * 2026-07-10.md §5's own "the form-sheet pattern is binding for ALL
   * three entities' CRUD UI" instruction). */
  mode?: "add" | "edit";
  /** Required when `mode === "edit"` — the contact being edited, to
   * pre-fill every field. */
  contact?: ContactOut;
  onAdd?: (fields: ContactFormSheetFields) => Promise<void>;
  onSave?: (fields: ContactFormSheetFields) => Promise<void>;
  onCancel: () => void;
}

/**
 * Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5) — the Contacts
 * Add/Edit bottom sheet: name (required), phone/email (optional, free
 * text — `contact.contact` is genuinely freeform per
 * `app/models/contractors.py`, unlike `AddPersonSheet`'s narrower field),
 * notes (optional). Same chrome/dismiss conventions as every other sheet in
 * this app and the same synchronous-reset-on-open discipline as
 * `AnimalFormSheet`/`ProjectFormSheet`/`AddPersonSheet`.
 *
 * No suggested-chips field here (unlike `ProjectFormSheet`'s status field)
 * — contacts have no analogous governed-vocabulary field to suggest
 * against.
 */
export function ContactFormSheet({
  open,
  mode = "add",
  contact,
  onAdd,
  onSave,
  onCancel,
}: ContactFormSheetProps) {
  const [name, setName] = useState(mode === "edit" ? (contact?.name ?? "") : "");
  const [contactField, setContactField] = useState(mode === "edit" ? (contact?.contact ?? "") : "");
  const [notes, setNotes] = useState(mode === "edit" ? (contact?.notes ?? "") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset SYNCHRONOUSLY on the closed->open transition — see
  // `ProjectFormSheet.tsx`'s docstring (itself citing `AnimalFormSheet.tsx`/
  // `AddPersonSheet.tsx`) for why this must never be a post-commit
  // `useEffect`.
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setName(mode === "edit" ? (contact?.name ?? "") : "");
    setContactField(mode === "edit" ? (contact?.contact ?? "") : "");
    setNotes(mode === "edit" ? (contact?.notes ?? "") : "");
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: nameRef,
  });

  if (!open) return null;

  const trimmedName = name.trim();

  async function submit() {
    if (!trimmedName) return;
    setSaving(true);
    setError(null);
    const fields: ContactFormSheetFields = {
      name: trimmedName,
      contact: contactField.trim() ? contactField.trim() : null,
      notes: notes.trim() ? notes.trim() : null,
    };
    try {
      if (mode === "edit") {
        await onSave?.(fields);
      } else {
        await onAdd?.(fields);
      }
    } catch (err) {
      // design-spec §5's "Inline validation / error states" — the
      // specific 409 name-conflict message, never the generic fallback,
      // for this one case (contacts share the same unique-name-index
      // posture as animals/projects — `uq_contractors_name`).
      if (err instanceof ApiError && err.status === 409) {
        setError(contactNameConflictMessage(trimmedName));
      } else {
        setError(err instanceof Error ? err.message : COULD_NOT_SAVE_ERROR);
      }
    } finally {
      setSaving(false);
    }
  }

  const titleId = "contact-form-sheet-title";

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id={titleId}>
          {mode === "edit" ? editContactSheetTitle(contact?.name ?? "") : ADD_CONTACT_SHEET_TITLE}
        </h2>

        <label class="admin-field-label" for="contact-form-name" style={{ marginTop: 0 }}>
          {CONTACT_NAME_FIELD_LABEL}
        </label>
        <input
          ref={nameRef}
          id="contact-form-name"
          type="text"
          class="pick-input"
          value={name}
          required
          aria-required="true"
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="contact-form-contact">
          {CONTACT_PHONE_OR_EMAIL_FIELD_LABEL}
        </label>
        <input
          id="contact-form-contact"
          type="text"
          class="pick-input"
          value={contactField}
          onInput={(e) => setContactField((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="contact-form-notes">
          {CONTACT_NOTES_FIELD_LABEL}
        </label>
        <textarea
          id="contact-form-notes"
          class="pick-input admin-textarea"
          value={notes}
          onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
        />

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!trimmedName || saving}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : mode === "edit" ? SAVE_CHANGES_LABEL : "Add"}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
